import React from 'react';

import { Checkbox } from 'ui/Checkbox';
import { CardInfo } from './CardInfo';
import { IbanInfo } from './IbanInfo';

import { InvoicesTableStyle } from 'styles/pages/settings.styles';
import { AddPaymentPopup } from 'pages/Settings/SettingsPage/SettingsPayment/PopupWindow';
import { STRIPE_CARD, requiresStripePaymentMethod } from 'constants/organization';
import {
    BillingInfoFragmentFragment,
    GetOrganizationQuery,
    GetUserQuery,
    OrganizationPaymentType,
} from 'data/__generated__';

interface PaymentCardsProps {
    organization: GetOrganizationQuery['organizationOfUser'] | null;
    billingInfo: BillingInfoFragmentFragment | null;
    user: GetUserQuery['user'] | null;
    hasCards: boolean;
    selectedCardIndex: number | null;
    originalCardIndex: number | null;
    setSelectedCardIndex: (index: number) => void;
    setStripeDefaultPaymentMethod: (args: any) => void;
    setSelectedCardModified: (modified: boolean) => void;
    popupWindowOpen: boolean;
    setPopupWindowOpen: (open: boolean) => void;
    invoiceValues: any;
}

export const PaymentCards: React.FC<PaymentCardsProps> = ({
    organization,
    billingInfo,
    user,
    hasCards,
    selectedCardIndex,
    originalCardIndex,
    setSelectedCardIndex,
    setStripeDefaultPaymentMethod,
    setSelectedCardModified,
    popupWindowOpen,
    setPopupWindowOpen,
    invoiceValues,
}: PaymentCardsProps) => {
    // Totem ne prélève pas cette organisation : on affiche son mode d'encaissement, jamais un
    // tableau de cartes Stripe qu'elle n'a aucune raison d'alimenter.
    const needsPaymentMethod = requiresStripePaymentMethod(organization?.paymentType);

    function handleSelectCard(index: number) {
        setSelectedCardModified(index !== originalCardIndex);
        setSelectedCardIndex(index);
    }

    return (
        <>
            <InvoicesTableStyle>
                <thead>
                    {/* Colonnes de cartes : uniquement quand on affiche des cartes. Une org que Totem ne
                        prélève pas montre la ligne de son mode d'encaissement, pas un titulaire ni une
                        expiration — même si elle traîne une carte enregistrée avant sa bascule. */}
                    {needsPaymentMethod && hasCards && (
                        <tr>
                            <th />
                            <th>Type de paiement</th>
                            <th>Mode de paiement</th>
                            <th>Nom du titulaire</th>
                            <th>Expiration</th>
                            <th>Supprimer</th>
                        </tr>
                    )}
                </thead>
                <tbody>
                    {!needsPaymentMethod ? (
                        <tr>
                            <td>
                                <Checkbox readOnly checked />
                            </td>
                            <td />
                            <td>
                                {organization?.paymentType === OrganizationPaymentType.Odoo
                                    ? 'Virement bancaire'
                                    : 'Aucun paiement requis'}
                            </td>
                            <td>{billingInfo?.invoiceName || organization?.name}</td>
                            <td />
                            <td />
                        </tr>
                    ) : (
                        hasCards &&
                        organization?.cards.map((paymentMethod: any, i: number) => (
                            <tr key={paymentMethod.id}>
                                <td>
                                    <Checkbox checked={selectedCardIndex === i} onChange={() => handleSelectCard(i)} />
                                </td>
                                {paymentMethod.card || paymentMethod.object === STRIPE_CARD ? (
                                    <CardInfo
                                        paymentMethod={paymentMethod}
                                        numberOfCards={organization?.cards.length}
                                        organizationId={organization?._id}
                                    />
                                ) : (
                                    <IbanInfo
                                        paymentMethod={paymentMethod}
                                        numberOfCards={organization.cards.length}
                                        organizationId={organization._id}
                                    />
                                )}
                            </tr>
                        ))
                    )}
                </tbody>
            </InvoicesTableStyle>
            {popupWindowOpen && (
                <AddPaymentPopup
                    organization={organization}
                    user={user}
                    setStripeDefaultPaymentMethod={setStripeDefaultPaymentMethod}
                    setPopupWindowOpen={setPopupWindowOpen}
                    invoiceValues={invoiceValues}
                />
            )}
        </>
    );
};
